"use client";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  getUserPersonalInfo,
  editProfileDataAPI,
} from "../../../../api/userApi";
import { queryKeys } from "../../../../query/keys";
import { useSubscriptionStyles } from "./styles";

export interface SubscriptionTier {
  id: string;
  tierName: string;
  price: number;
  durationInDays: 30 | 90 | 180;
}

export interface EarningsHistoryItem {
  amount: number;
  type: "subscription" | "ppv" | "custom" | "tip";
  date: Date;
  payoutStatus: boolean;
}

export interface UserPricingData {
  subscriptionPrice: SubscriptionTier[];
  ppvRate: number;
  customContentPrice: number;
  tipMinimum: number;
  earningsHistory: EarningsHistoryItem[];
}

const SubscriptionTierPricing: React.FC = () => {
  const classes = useSubscriptionStyles();
  const queryClient = useQueryClient();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([
    { id: "1", tierName: "Premium", price: 50, durationInDays: 30 },
    //{ id: "2", tierName: "Middle", price: 140, durationInDays: 90 },
    // { id: "3", tierName: "Premium", price: 280, durationInDays: 180 },
  ]);
  const { data: userPricingData, isLoading } = useQuery({
    queryKey: queryKeys.personalInfo,
    queryFn: async () => {
      const res = await getUserPersonalInfo();
      return res.data as UserPricingData;
    },
  });

  const saveSubscriptionTiersMutation = useMutation({
    mutationFn: (formData: FormData) => editProfileDataAPI(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.personalInfo });
      toast.success("Subscription tiers updated successfully");
    },
    onError: error => {
      toast.error("Failed to update subscription tiers");
      console.error(error);
    },
  });

  useEffect(() => {
    if (!userPricingData) {
      return;
    }

    // Оновлюємо лише ціни, зберігаючи фіксовані назви та тривалості
    setTiers([
      {
        id: "1",
        tierName: "Premium",
        price: userPricingData.subscriptionPrice[0]?.price || 50,
        durationInDays: 30,
      },
      //  { id: "2", tierName: "Middle", price: userPricingData.subscriptionPrice[1]?.price || 140, durationInDays: 90 },
      //  { id: "3", tierName: "Premium", price: userPricingData.subscriptionPrice[2]?.price || 280, durationInDays: 180 },
    ]);
  }, [userPricingData]);

  const updateTierPrice = (id: string, price: number) => {
    setTiers(tiers.map((tier) => (tier.id === id ? { ...tier, price } : tier)));
  };

  const handleSave = () => {
    const formData = new FormData();
    tiers.forEach((tier, index) => {
      formData.append(`subscriptionPrice[${index}][tierName]`, tier.tierName);
      formData.append(
        `subscriptionPrice[${index}][price]`,
        tier.price.toString(),
      );
      formData.append(
        `subscriptionPrice[${index}][durationInDays]`,
        tier.durationInDays.toString(),
      );
    });

    saveSubscriptionTiersMutation.mutate(formData);
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>
        Subscription Tier Pricing
      </Typography>
      <Typography className={classes.infoText}>
        Set the prices for your subscription tiers. Durations are fixed, and
        prices are publicly visible.
      </Typography>

      {tiers.map((tier) => (
        <Card key={tier.id} className={classes.tierCard}>
          <CardContent className={classes.cardContent}>
            <Box className={classes.tierHeader}>
              <Typography className={classes.tierName}>
                {tier.tierName}
              </Typography>
            </Box>

            <Box className={classes.formRow}>
              <Typography className={classes.label}>Tier Name</Typography>
              <TextField
                className={classes.inputField}
                variant="outlined"
                size="small"
                value={tier.tierName}
                disabled // Фіксована назва
              />
            </Box>

            <Box className={classes.formRow}>
              <Typography className={classes.label}>Price ($)</Typography>
              <TextField
                className={classes.inputField}
                variant="outlined"
                size="small"
                type="number"
                value={tier.price}
                onChange={(e) =>
                  updateTierPrice(tier.id, Number(e.target.value))
                }
                placeholder="Enter price"
              />
            </Box>

            <Box className={classes.formRow}>
              <Typography className={classes.label}>Duration (Days)</Typography>
              <TextField
                className={classes.inputField}
                variant="outlined"
                size="small"
                value={`${tier.durationInDays} (${tier.durationInDays / 30} month${tier.durationInDays !== 30 ? "s" : ""})`}
                disabled // Фіксована тривалість
              />
            </Box>
          </CardContent>
        </Card>
      ))}

      <Button
        className={classes.saveButton}
        onClick={handleSave}
        disabled={saveSubscriptionTiersMutation.isPending}
      >
        {saveSubscriptionTiersMutation.isPending ? (
          <>
            <CircularProgress size={16} color="inherit" />
            &nbsp;Saving...
          </>
        ) : (
          "Save Changes"
        )}
      </Button>
    </div>
  );
};

export default SubscriptionTierPricing;
